import type { FileSystemEntry } from "./opencodeTypes"

// @ file mention autocomplete for the chat composer.
//
// Pure functions, no React. The agent resolves `@path` server-side via regex
// in its prompt parser — we only need to surface the right path to the user
// and insert it into the textarea.

export interface AtMention {
  /** The raw query text after the `@` (e.g. "src/file" from "@src/file"). */
  query: string
  /** Character index of the `@` in the full text. */
  startIndex: number
  /** Character index just past the end of the query (where the caret is). */
  endIndex: number
}

/**
 * Extract an `@query` immediately before the caret.
 *
 * Returns undefined when the caret is not inside an active mention — i.e. there
 * is no `@` between the caret and the nearest whitespace.
 */
export function parseAtMention(text: string, cursorPos: number): AtMention | undefined {
  if (cursorPos < 1) return undefined
  // Walk backwards from the cursor to find an `@` that is not preceded by a
  // word character (so `email@host` does not trigger).
  let i = cursorPos - 1
  while (i >= 0) {
    const ch = text[i] ?? ""
    if (ch === "@") {
      // Must be at the start of the text or preceded by whitespace / punctuation
      // that is not a word character.
      const prev: string = i > 0 ? (text[i - 1] ?? "") : ""
      if (i === 0 || /[\s,;:'"([{]/.test(prev)) {
        const query = text.slice(i + 1, cursorPos)
        // No whitespace inside the query — the agent's own parser stops at space.
        if (!/\s/.test(query)) {
          return { query, startIndex: i, endIndex: cursorPos }
        }
      }
      return undefined
    }
    // Stop at whitespace — the mention cannot span across a space.
    if (/\s/.test(ch)) return undefined
    i--
  }
  return undefined
}

/**
 * Filter and rank file entries against a query.
 *
 * Simple substring match on the path, case-insensitive. Entries whose path
 * contains every segment of the query (in order) rank higher.
 */
export function matchFiles(entries: FileSystemEntry[], query: string): FileSystemEntry[] {
  const needle = query.toLowerCase()
  if (!needle) return entries.slice(0, 20)

  const segments = needle.split("/").filter(Boolean)

  return entries
    .map((entry) => {
      const path = entry.path.toLowerCase()
      const containsAll = segments.every((seg) => path.includes(seg))
      // Boost exact prefix matches and single-segment matches.
      const score = containsAll
        ? path.startsWith(needle)
          ? 2
          : segments.length === 1
            ? 1
            : 0
        : -1
      return { entry, score }
    })
    .filter((r) => r.score >= 0)
    .sort((a, b) => b.score - a.score || a.entry.path.localeCompare(b.entry.path))
    .slice(0, 20)
    .map((r) => r.entry)
}

/**
 * Replace the `@query` range in `text` with `@path `.
 *
 * Returns the new text and the new cursor position (just after the inserted
 * mention and trailing space).
 */
export function insertMention(
  text: string,
  mention: AtMention,
  path: string,
): { text: string; cursorPos: number } {
  const replacement = `@${path} `
  const newText = text.slice(0, mention.startIndex) + replacement + text.slice(mention.endIndex)
  return { text: newText, cursorPos: mention.startIndex + replacement.length }
}

/**
 * Format a file path for display in the autocomplete popover.
 *
 * Strips trailing slashes from directories and shows only the last two segments
 * when the full path is long.
 */
export function formatFilePath(path: string): string {
  const cleaned = path.replace(/\/+$/, "")
  const parts = cleaned.split("/")
  if (parts.length <= 2) return cleaned
  return `…/${parts.slice(-2).join("/")}`
}
